//WhistleBlowArchiveDialog
//***********************************************************************************************************************
import { Box } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { WhistleBlowArchivingType, WhistleBlowState } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IListFilterItem, ListFilterItem } from "../../../models/common/listFilterItem";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { WhistleBlowArchiveSelectTypeDialog } from "../WhistleBlowArchiveSelectTypeDialog/WhistleBlowArchiveSelectTypeDialog";
import { WhistleBlowArchiveWhistleBlowList } from "../WhistleBlowArchiveWhistleBlowList";
import { WhistleBlowFilter } from "../WhistleBlowFilter";
import { IWhistleBlowPageFilterState, WhistleBlowPageDashboardChart } from "../WhistleBlowPageDashboardChart";
import { List } from "source/components/framework/List/List";

//WhistleBlowArchiveDialog
export interface IWhistleBlowArchiveDialogProps {
    classes?: string;
    companyId: string;
    units: IIdTitleNode[];
    categories: IIdTitle[];
    handlers: IIdTitle[];
    stateFilters: IIdTitle[];
    severityFilters: IIdTitle[];
    resolutionFilters: IIdTitle[];
    sourceFilters: IIdTitle[];
    anonymityFilters: IIdTitle[];
    dynamicTimeFilters: IIdTitle[];
    onOk: () => void;
    onCancel: () => void;
}

export interface IWhistleBlowArchiveDialogState extends IWhistleBlowPageFilterState {
    selectedId: string;
    checkedIds: string[];
    showWhistleBlowFilterDialog: boolean;
    whistleBlowFilterDialogFilter: IListFilterItem;
    whistleBlowFilterIsSet: boolean;
    showWhistleBlowArchiveSelectTypeDialog: boolean;
}

export class WhistleBlowArchiveDialog extends React.Component<IWhistleBlowArchiveDialogProps, IWhistleBlowArchiveDialogState> {
    constructor(props: IWhistleBlowArchiveDialogProps) {
        super(props);
        this.state = {
            selectedId: null,
            checkedIds: [],
            selectedStartDate: null,
            selectedEndDate: null,
            selectedUnitIds: [props.units[0].id],
            selectedCategoryIds: [props.categories[0].id],
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedStateFilterId: WhistleBlowState.Resolved.toString(10),
            selectedSeverityFilterId: props.severityFilters[0].id,
            selectedResolutionFilterId: props.resolutionFilters[0].id,
            selectedSourceFilterId: props.sourceFilters[0].id,
            selectedAnonymityFilterId: props.anonymityFilters[0].id,
            selectedDynamicTimeFilterId: props.dynamicTimeFilters[0].id,
            showWhistleBlowFilterDialog: false,
            whistleBlowFilterDialogFilter: null,
            whistleBlowFilterIsSet: false,
            showWhistleBlowArchiveSelectTypeDialog: false
        };
    }

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleCancel = () => {
        this.props.onCancel();
    };

    static getSaveData = (archivingType: WhistleBlowArchivingType, whistleBlowIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("ids", whistleBlowIds);
        result.append("archivingType", archivingType);
        return result;
    };

    static archiveWhistleBlows = async (archivingType: WhistleBlowArchivingType, whistleBlowIds: string[]): Promise<SaveSuccess> => {
        if (whistleBlowIds.length < 1) return;
        const saveData = WhistleBlowArchiveDialog.getSaveData(archivingType, whistleBlowIds);
        // Call server
        return await AppUtils.callService(() => whistleBlowService.archiveWhistleBlows(saveData.obj));
    };

    // #region Archiving Type
    handleOk = async () => {
        this.setState({
            showWhistleBlowArchiveSelectTypeDialog: true
        });
    };

    handleWhistleBlowArchiveSelectTypeDialogOk = async (archivingType: WhistleBlowArchivingType) => {
        const state = this.state;
        const result = await WhistleBlowArchiveDialog.archiveWhistleBlows(archivingType, List.getSelectedIds(state.selectedId, state.checkedIds));
        if (!result) return null;
        this.props.onOk();
    };
    
    handleWhistleBlowArchiveSelectTypeDialogCancel = () => {
        this.setState({
            showWhistleBlowArchiveSelectTypeDialog: false
        });
    };
    
    // #endregion Archiving Type

    // #region Record Filter
    getWhistleBlowFilterIsSet = (state: IWhistleBlowArchiveDialogState): boolean => {
        const props = this.props;
        return WhistleBlowFilter.filterIsSet(props.units, props.categories, props.handlers, props.stateFilters, props.severityFilters, props.resolutionFilters, props.sourceFilters, props.anonymityFilters, props.dynamicTimeFilters,
            state.selectedStartDate, state.selectedEndDate, state.selectedUnitIds, state.selectedCategoryIds, state.selectedHandlerUserCodes, props.stateFilters[0].id, state.selectedSeverityFilterId,
            state.selectedResolutionFilterId, state.selectedSourceFilterId, state.selectedAnonymityFilterId, state.selectedDynamicTimeFilterId);
    };

    handleOpenWhistleBlowFilterDialog = () => {
        this.setState({
            showWhistleBlowFilterDialog: true,
            whistleBlowFilterDialogFilter: ListFilterItem.createListFilterItem(Base.getGuid(), Translations.ArchiveWhistleBlows, WhistleBlowPageDashboardChart.getGeneralFilterFromState(this.state), "state", true)
        });
    };

    onWhistleBlowFilterDialogOk = (closeDialog: boolean, selectedStartDate: number, selectedEndDate: number, selectedStateFilterId: string, selectedSeverityFilterId: string, selectedResolutionFilterId: string,
        selectedSourceFilterId: string, selectedAnonymityFilterId: string, selectedDynamicTimeFilterId: string, selectedCategoryIds: string[], selectedUnitIds: string[]): any => {
        const newState = { ...this.state, selectedStartDate, selectedEndDate, selectedStateFilterId, selectedSeverityFilterId, selectedResolutionFilterId, selectedSourceFilterId, selectedAnonymityFilterId, selectedDynamicTimeFilterId, selectedCategoryIds, selectedUnitIds };
        this.setState({
            showWhistleBlowFilterDialog: !closeDialog,
            selectedStartDate,
            selectedEndDate,
            //selectedStateFilterId,
            selectedSeverityFilterId,
            selectedResolutionFilterId,
            selectedSourceFilterId,
            selectedAnonymityFilterId,
            selectedDynamicTimeFilterId,
            selectedCategoryIds,
            selectedUnitIds,
            whistleBlowFilterIsSet: this.getWhistleBlowFilterIsSet(newState),
            //whistleBlowFilterActions: this.getWhistleBlowFilterActions(newState)
        });
        return WhistleBlowPageDashboardChart.getGeneralFilterFromState(newState);
    };
    
    onWhistleBlowFilterDialogCancel = () => {
        this.setState({ showWhistleBlowFilterDialog: false });
    };
    // #endregion Record Filter

    render() {
        const props = this.props;
        const state = this.state;

        return (
            <Dialog
                id="wb-archive-dialog"
                maxWidth="lg"
                maxHeight="726px"
                open
                title={Translations.ArchiveWhistleBlows}
                body={
                    <Box display="flex" flexDirection="column">
                        <ActionContainer
                            rightItems={
                                <ListActionButtons onFilter={this.handleOpenWhistleBlowFilterDialog} />
                            }
                        />
                        <WhistleBlowArchiveWhistleBlowList
                            companyId={props.companyId}
                            selectedStartDate={state.selectedStartDate}
                            selectedEndDate={state.selectedEndDate}
                            selectedUnitIds={state.selectedUnitIds}
                            selectedCategoryIds={state.selectedCategoryIds}
                            selectedHandlerUserCodes={state.selectedHandlerUserCodes}
                            selectedStateFilterId={state.selectedStateFilterId}
                            selectedSeverityFilterId={state.selectedSeverityFilterId}
                            selectedResolutionFilterId={state.selectedResolutionFilterId}
                            selectedSourceFilterId={state.selectedSourceFilterId}
                            selectedAnonymityFilterId={state.selectedAnonymityFilterId}
                            selectedDynamicTimeFilterId={state.selectedDynamicTimeFilterId}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                        />
                        {state.showWhistleBlowFilterDialog &&
                        <Dialog
                            id="wb-filter-records-dialog"
                            maxWidth="lg"
                            title={Translations.FilterCondition}
                            body={
                                <WhistleBlowFilter
                                    filter={state.whistleBlowFilterDialogFilter}
                                    units={props.units}
                                    categories={props.categories}
                                    handlers={props.handlers}
                                    stateFilters={props.stateFilters}
                                    severityFilters={props.severityFilters}
                                    resolutionFilters={props.resolutionFilters}
                                    sourceFilters={props.sourceFilters}
                                    anonymityFilters={props.anonymityFilters}
                                    dynamicTimeFilters={props.dynamicTimeFilters}
                                    selectedStartDate={state.selectedStartDate}
                                    selectedEndDate={state.selectedEndDate}
                                    selectedUnitIds={state.selectedUnitIds}
                                    selectedCategoryIds={state.selectedCategoryIds}
                                    selectedHandlerUserCodes={state.selectedHandlerUserCodes}
                                    selectedStateFilterId={state.selectedStateFilterId}
                                    selectedSeverityFilterId={state.selectedSeverityFilterId}
                                    selectedResolutionFilterId={state.selectedResolutionFilterId}
                                    selectedSourceFilterId={state.selectedSourceFilterId}
                                    selectedAnonymityFilterId={state.selectedAnonymityFilterId}
                                    selectedDynamicTimeFilterId={state.selectedDynamicTimeFilterId}
                                    canRemoveSharedFilters={false}
                                    stateIsReadOnly={true}
                                    onSaveFilter={null}
                                    onRemoveFilter={null}
                                    size="lg"
                                    onOk={this.onWhistleBlowFilterDialogOk}
                                    onCancel={this.onWhistleBlowFilterDialogCancel}
                                />
                            }
                            onClose={this.handleCancel}
                        />
                        }
                        {state.showWhistleBlowArchiveSelectTypeDialog &&
                            <WhistleBlowArchiveSelectTypeDialog
                                onOk={this.handleWhistleBlowArchiveSelectTypeDialogOk}
                                onCancel={this.handleWhistleBlowArchiveSelectTypeDialogCancel}
                            />
                        }
                    </Box>
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleOk },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}